import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAppDispatch } from '../../../../configuration/setup/store';
import { useGetContractsQuery } from '../../api/contracts/contractApiSlice';
import { fetchAllNonLegacyProducts } from '../../api/product/productCalls';
import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { fetchAssets } from '../../api/tags/tagCalls';
import type { Product } from '../../checkout/redux/types';
import { fetchDiscountsThunk } from '../../common/discount/discounts.thunk';
import { RenewDialogContainer } from '../../common/termedLevel/dialog/RenewDialog';
import { BookingOverviewProductListContainer } from './BookingOverviewProductList';
import {
    BookingOverviewEmptyState,
    BookingOverviewErrorState,
    BookingOverviewForbiddenState,
} from './BookingOverviewStates';
import { BookingOverviewStats } from './BookingOverviewStats';
import { getBookingOverviewStatistics } from './bookingOverviewService';

export function BookingOverviewContainer() {
    const dispatch = useAppDispatch();
    const {
        data: contracts,
        error: contractsError,
        isError: hasContractsError,
        isFetching,
        refetch,
    } = useGetContractsQuery(undefined);
    const [assets, setAssets] = useState<ApiAssetInfo[]>();
    const [products, setProducts] = useState<Product[]>();
    const [hasError, setHasError] = useState(false);
    const [reloadCount, setReloadCount] = useState(0);

    const reload = () => {
        setAssets(undefined);
        setProducts(undefined);
        setHasError(false);
        setReloadCount(count => count + 1);
        refetch();
    };

    useEffect(() => {
        dispatch(fetchDiscountsThunk());
    }, [dispatch, reloadCount]);

    useEffect(() => {
        let isMounted = true;
        Promise.all([fetchAssets(), fetchAllNonLegacyProducts()])
            .then(([assets, products]) => {
                if (isMounted) {
                    setAssets(assets);
                    setProducts(products);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setHasError(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [reloadCount]);

    if (hasContractsError && !isFetching) {
        if (
            contractsError &&
            'status' in contractsError &&
            (contractsError.status === 401 || contractsError.status === 403)
        ) {
            return <BookingOverviewForbiddenState />;
        }

        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (isFetching || !contracts) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    if (contracts.length === 0) {
        return <BookingOverviewEmptyState />;
    }

    if (hasError) {
        return <BookingOverviewErrorState onReload={reload} />;
    }

    if (!assets || !products) {
        return <Spinner text={<FormattedMessage id='intl-msg:marketplace.loading' />} isInverse={false} />;
    }

    return (
        <>
            <BookingOverviewStats statistics={getBookingOverviewStatistics(contracts, assets)} />
            <BookingOverviewProductListContainer contracts={contracts} products={products} />
            <RenewDialogContainer titleId='intl-msg:common-message.contracts.renewal' />
        </>
    );
}
